import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Error } from 'src/app/models/error.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { Router } from '@angular/router';
import { Service } from 'src/app/models/service.model';
import { Services } from 'src/app/services/services/services.service';
import { BALISETYPE, TaggedService } from 'src/app/models/taggedService.model';
import { TranslateService } from '@ngx-translate/core';
import { UserInfo } from 'src/app/models/user-info.model';
import { UsersService } from 'src/app/services/users/users.service';
import { entAnimations } from 'src/app/animations';
import { lastValueFrom, map, mergeMap } from 'rxjs';
import { DashboardResetComponent } from './dashboard-reset-confirm/dashboard-reset-confirm';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { MaintenanceListFields } from 'src/app/models/maintenance-list-fields.models';
import { MatomoTracker } from 'ngx-matomo-client';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { ServiceTranslated } from 'src/app/models/serviceTranslated';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { DISPLAYMODE } from 'src/app/models/user-settings.model';
import { SettingsService } from 'src/app/services/settings/settings.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class DashboardComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  isSuccess = false;
  error: Error;
  isLoading = false;
  selectedUser: UserInfo = new UserInfo();
  currentLang: string;
  searchText: string;
  mustChangemail: boolean;
  private services: Service[] = [];
  filterServicesTranslated: ServiceTranslated[] = [];
  maintenanceMessage: MaintenanceListFields[];
  display: number;
  private taggedServices: TaggedService[] = [];
  private sortInternal: Sort | null;
  showHiddenServices = false;
  dataSource: MatTableDataSource<ServiceTranslated>;

  displayedColumns: string[] = [];
  expandedElement: Service | null;
  windowWidth: number = window.innerWidth;
  sortedValue: string;

  constructor(
    private _userService: UsersService,
    private _settingsService: SettingsService,
    private _router: Router,
    private _graphService: GraphService,
    private _services: Services,
    private _matDialog: MatDialog,
    private readonly tracker: MatomoTracker,
    _translateService: TranslateService,
    private _localStorageService: LocalStorageService
  ) {
    this.getListofMaintenancesMessages();

    this.currentLang = _translateService.currentLang;

    _translateService.onLangChange.subscribe(l => {
      this.currentLang = l.lang;
      if (this.filterServicesTranslated) {
        this.filterServicesTranslated = this.getFilterServicesTranslated();
        //Triple sorting, could use some refactor
        this.sortServices();
        this.sortByTag(this.filterServicesTranslated);
        if (this.sortInternal) {
          this.sortData(this.sortInternal);
        }
      }
      if (this.dataSource) this.dataSource.data = this.filterServicesTranslated;
    });
  }

  navigateToPage(): void {
    window.location.href =
      'https://employee.vs.ch?saml2idp=https%3A%2F%2Fedusso.apps.vs.ch%2Fauth%2Frealms%2Fictvs';
  }

  private getFilterServicesTranslated(): ServiceTranslated[] {
    const isLangGerman = this.currentLang === 'de';
    return this.services.map(service => ({
      oid: service.oid,
      name: service.name,
      jpegPhoto: service.jpegPhoto,
      title: isLangGerman ? service.title_de : service.title_fr,
      description: isLangGerman
        ? service.description_de
        : service.description_fr,
      help: isLangGerman ? service.help_de : service.help_fr,
      accessurl: isLangGerman ? service.accessurl_de : service.accessurl_fr,
      priority: service.priority,
      subtitle: isLangGerman ? service.subtitle_de : service.subtitle_fr,
      subtype: service.subtype,
      maintenance: service.maintenance,
      tagId: service.tagId,
    }));
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.mustChangemail = false;
    this.searchText = '';

    this.getUserAndServices();
    this.updateDisplayedColumns();

    if (this.selectedUser.email) {
      if (
        this.selectedUser.email.includes('@edu.vs.ch') ||
        this.selectedUser.email.includes('educanet2.ch')
      ) {
        this.mustChangemail = true;
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  updateDisplayedColumns(): void {
    if (window.innerWidth < 400) {
      this.displayedColumns = ['title', 'accessurl', 'expand'];
    } else if (window.innerWidth >= 400 && window.innerWidth < 550) {
      this.displayedColumns = ['jpegPhoto', 'title', 'accessurl', 'expand'];
    } else if (window.innerWidth >= 550 && window.innerWidth < 650) {
      this.displayedColumns = [
        'jpegPhoto',
        'title',
        'tagId',
        'accessurl',
        'expand',
      ];
    } else if (window.innerWidth >= 650 && window.innerWidth < 850) {
      this.displayedColumns = [
        'jpegPhoto',
        'title',
        'help',
        'tagId',
        'accessurl',
        'expand',
      ];
    } else {
      this.displayedColumns = [
        'jpegPhoto',
        'title',
        'description',
        'help',
        'tagId',
        'accessurl',
      ];
    }
  }

  async getUserDisplayValue() {
    if (
      this._localStorageService.getItem<string>('userDefaultDisplayMode') ===
      null
    ) {
      await lastValueFrom(
        this._settingsService.getUserSettings(this.selectedUser.oid)
      ).then(us => {
        if (us !== null) {
          this.display = us.displayModeId;
        } else {
          this.display = DISPLAYMODE.Maxi;
        }
      });
    } else {
      this.display = this._localStorageService.getItem<number>(
        'userDefaultDisplayMode'
      );
    }
  }

  changeDisplay(displayValue: number) {
    this.display = displayValue;
    this._localStorageService.setItem('userDefaultDisplayMode', this.display);
    this.searchText = '';
  }

  applyFilter(event: KeyboardEvent) {
    const filterValue = event.target as HTMLInputElement;
    this.dataSource.filter = filterValue.value.trim().toLocaleLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  openService(service: ServiceTranslated): void {
    this.tracker.trackEvent('Service', 'Open', service.name);
    this._services.openService(service.accessurl, service.title);
  }

  openDocumentation(service: ServiceTranslated): void {
    this._services.openDocumentation(service.help);
  }

  goToIddentity() {
    this._router.navigate(['/self']);
  }

  confirmReset() {
    const dialogRef = this._matDialog.open(DashboardResetComponent, {
      panelClass: 'app-dashboard-reset-confirm-dialog',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((response: boolean) => {
      if (response === true) {
        this.resetDashboard();
      }
    });
  }

  async resetDashboard(): Promise<void> {
    if (this.taggedServices.length == 0) return;

    this.isLoading = true;
    this._services.resetTaggedServices().subscribe({
      next: () => {
        this.taggedServices = [];
        this.services.forEach(s => {
          s.tagId = BALISETYPE.None;
        });

        this.filterServicesTranslated.forEach(s => {
          s.tagId = BALISETYPE.None;
        });

        this.isLoading = false;
        this.isSuccess = true;
      },
      error: (e: HttpErrorResponse) => {
        let customError: Error = { error: undefined };

        if (e.statusText == 'Unknown Error') {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (e.error) {
            customError = JSON.parse(e.error);
          } else {
            customError = {
              error: { message: 'Unknown Error', code: 'ERR2' },
            };
          }
          if (customError.error === undefined) {
            customError = {
              error: { message: 'Unknown Error', code: 'ERR2' },
            };
          }
        }

        this.isSuccess = false;
        this.isLoading = false;
        this.error = customError;
      },
    });
  }

  async addFavorite(service: ServiceTranslated) {
    const taggedService: TaggedService = {
      userOid: this.selectedUser.oid,
      serviceOid: service.oid,
      baliseId: BALISETYPE.Pinned,
      date: new Date(),
    };
    this._services.addTaggedService(taggedService).subscribe(() => {
      this.taggedServices.push(taggedService);
      this.services.find(s => s.oid == service.oid).tagId = BALISETYPE.Pinned;
      this.filterServicesTranslated.find(s => s.oid == service.oid).tagId =
        BALISETYPE.Pinned;
    });
  }

  async addHidden(service: ServiceTranslated) {
    const taggedService: TaggedService = {
      userOid: this.selectedUser.oid,
      serviceOid: service.oid,
      baliseId: BALISETYPE.Hidden,
      date: new Date(),
    };
    this._services.addTaggedService(taggedService).subscribe(() => {
      this.taggedServices.push(taggedService);
      this.services.find(s => s.oid == service.oid).tagId = BALISETYPE.Hidden;
      this.filterServicesTranslated.find(s => s.oid == service.oid).tagId =
        BALISETYPE.Hidden;
    });
  }

  async removeTag(service: ServiceTranslated) {
    const itemToDeleteIndex = this.taggedServices.findIndex(
      s => s.serviceOid === service.oid
    );
    this._services
      .deleteTaggedService(this.taggedServices[itemToDeleteIndex])
      .subscribe(() => {
        this.taggedServices[itemToDeleteIndex].baliseId = BALISETYPE.None;
        this.services.find(s => s.oid == service.oid).tagId = BALISETYPE.None;
        this.filterServicesTranslated.find(s => s.oid == service.oid).tagId =
          BALISETYPE.None;
      });
  }

  getFavoriteServices(): ServiceTranslated[] {
    return this.filterServicesTranslated.filter(
      x => x.tagId === BALISETYPE.Pinned
    );
  }

  getHiddenServices(): ServiceTranslated[] {
    return this.filterServicesTranslated.filter(
      x => x.tagId === BALISETYPE.Hidden
    );
  }

  getOtherServices(): ServiceTranslated[] {
    return this.filterServicesTranslated.filter(
      x => x.tagId === BALISETYPE.None
    );
  }

  toggleVisibilityHiddenServices() {
    this.showHiddenServices = !this.showHiddenServices;
  }

  async getListofMaintenancesMessages(): Promise<void> {
    try {
      this.maintenanceMessage = await lastValueFrom(
        this._graphService.getListMaintenanceMessage()
      );
      // Filter location = Front page
      this.maintenanceMessage = this.maintenanceMessage.filter(
        x => x.location == 'Dashboard' || x.location == 'Service'
      );

      this.maintenanceMessage = this.maintenanceMessage.filter(
        x =>
          this.services.some(service => service.oid === x.service_oid) ||
          x.location === 'Dashboard'
      );
    } catch {
      // Empty
    }
  }

  getUserAndServices() {
    this.isLoading = true;
    this._userService
      .getCurrentUserFromStorage()
      .pipe(
        map(user => {
          this.selectedUser = user;
          this.getUserDisplayValue();

          // Keep All Pedagogical Services
          this.services = this.selectedUser.services.filter(
            element =>
              element.subtype.includes('pedagogique') ||
              element.subtype.includes('pedagogique-edulog')
          );

          return user.oid;
        }),
        mergeMap(userOid => this._services.getTaggedServices(userOid))
      )
      .subscribe({
        next: s => {
          this.taggedServices = s;
          this.services = this.services.map(service => ({
            ...service,
            tagId: this.taggedServices.some(ts => ts.serviceOid === service.oid)
              ? this.taggedServices.find(ts => ts.serviceOid === service.oid)
                  .baliseId
              : 0,
          }));

          this.filterServicesTranslated = this.getFilterServicesTranslated();
          this.sortServices();

          this.isLoading = false;

          setTimeout(() => {
            this.dataSource = new MatTableDataSource(
              this.sortByTag(this.filterServicesTranslated)
            );
            this.dataSource.paginator = this.paginator;
          });
        },
        error: e => {
          this.taggedServices = [];
          this.isLoading = false;

          let customError: Error = { error: undefined };
          if (e.error) {
            customError = JSON.parse(e.error);
          }
          if (customError.error !== undefined) {
            if (customError.error.code == 'ERR403') {
              this._router.navigate(['/errors/error-403']);
            } else {
              this._router.navigate(['/errors/error-404']);
            }
          }
        },
      });
  }

  sortServices() {
    this.filterServicesTranslated = this.filterServicesTranslated.sort(
      (a: ServiceTranslated, b: ServiceTranslated) => {
        return a.priority - b.priority;
      }
    );
  }

  sortData(sort: Sort) {
    this.sortInternal = sort;
    this.sortedValue = sort.active;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    if (!sort.active || sort.direction === '') {
      this.sortedValue = null;
      this.sortServices();
      this.dataSource.data = this.sortByTag(this.filterServicesTranslated);
      return;
    }

    this.dataSource.data = this.filterServicesTranslated.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'title':
          return this.compare(
            a.title.toLocaleLowerCase(),
            b.title.toLocaleLowerCase(),
            isAsc
          );
      }
    });
  }

  sortByTag(items: ServiceTranslated[]) {
    if (!items) return [];
    return items.sort((a, b) => {
      // Ordre de tri: favoris, autres, cachés
      const order = { 1: 0, 0: 1, 2: 2 };
      return order[a.tagId] - order[b.tagId];
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleRow(element: Service): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
